import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Download,
  Filter,
  AlertTriangle,
  FileText,
  BarChart3
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface LeaveReportData {
  summary: {
    totalRequests: number
    approvedRequests: number
    pendingRequests: number
    averageLeaveDays: number
  }
  leaveTypeDistribution: Array<{
    leaveType: string
    count: number
    percentage: number
    averageDays: number
  }>
  departmentAnalysis: Array<{
    department: string
    totalRequests: number
    approvalRate: number
    averageDays: number
  }>
  monthlyTrend: Array<{
    month: string
    requests: number
    approvalRate: number
    popularType: string
  }>
  utilization: {
    totalAllocated: number
    totalUsed: number
    utilizationRate: number
    byLeaveType: Array<{ type: string; allocated: number; used: number; rate: number }>
  }
}

const LeaveReports: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })
  const [view, setView] = useState('overview')

  const { data: leaveData, isLoading } = useQuery({
    queryKey: ['leave-reports', dateRange],
    queryFn: async (): Promise<LeaveReportData> => {
      const response = await api.get('/reports/leave-detailed', {
        params: dateRange
      })
      return response.data.data
    }
  })

  const summaryCards = [
    {
      title: 'Total Requests',
      value: leaveData?.summary.totalRequests || 0,
      description: 'All leave requests',
      icon: Calendar,
      color: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
    },
    {
      title: 'Approved',
      value: leaveData?.summary.approvedRequests || 0,
      description: `${((leaveData?.summary.approvedRequests || 0) / (leaveData?.summary.totalRequests || 1) * 100).toFixed(1)}% rate`,
      icon: TrendingUp,
      color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
    },
    {
      title: 'Pending',
      value: leaveData?.summary.pendingRequests || 0,
      description: 'Awaiting approval',
      icon: Clock,
      color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
    },
    {
      title: 'Avg Duration',
      value: `${leaveData?.summary.averageLeaveDays || 0} days`,
      description: 'Per request',
      icon: Users,
      color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
    }
  ]

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const response = await api.get(`/reports/leave-detailed/export`, {
        params: { ...dateRange, format },
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `leave-report-${new Date().toISOString().split('T')[0]}.${format}`
      link.click()
      window.URL.revokeObjectURL(url)

      toast.success(`Leave report exported as ${format.toUpperCase()}`)
    } catch (error) {
      toast.error('Failed to export report')
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Leave Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Comprehensive leave management analytics and insights
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <span className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium">
            {leaveData?.summary.totalRequests || 0} Total Requests
          </span>
          <span className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 rounded-lg text-xs font-medium">
            {((leaveData?.summary.approvedRequests || 0) / (leaveData?.summary.totalRequests || 1) * 100).toFixed(1)}% Approval Rate
          </span>
        </div>
      </div>

      {/* Filters and Export Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Date Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2">
              <Filter className="h-3 w-3 text-gray-400" />
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="text-xs border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="text-xs border-none focus:outline-none bg-transparent text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-all duration-200"
            >
              <Download size={12} />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Download size={12} />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {['overview', 'types', 'departments', 'utilization'].map((tab) => (
          <button
            key={tab}
            onClick={() => setView(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === tab
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {summaryCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{card.title}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{card.value}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.description}</p>
                </div>
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${card.color}`}>
                  <Icon size={18} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Report Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Leave Type Distribution */}
        {view === 'types' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FileText size={14} className="text-indigo-600 dark:text-indigo-400" />
              Leave Type Distribution
            </h3>
            <div className="space-y-3">
              {leaveData?.leaveTypeDistribution.map((type, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white text-xs">{type.leaveType}</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">Avg: {type.averageDays} days</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white text-xs">{type.count} requests</div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">{type.percentage}% of total</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Department Analysis */}
        {view === 'departments' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Users size={14} className="text-indigo-600 dark:text-indigo-400" />
              Department Analysis
            </h3>
            <div className="space-y-3">
              {leaveData?.departmentAnalysis.map((dept, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white text-xs">{dept.department}</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">Avg: {dept.averageDays} days</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 dark:text-white text-xs">{dept.totalRequests} requests</div>
                    <div className={`text-xs ${dept.approvalRate >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {dept.approvalRate}% approval
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Utilization Analysis */}
        {view === 'utilization' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card lg:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-indigo-600 dark:text-indigo-400" />
              Leave Utilization
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                  <div className="text-base font-bold text-indigo-700 dark:text-indigo-400">{leaveData?.utilization.totalAllocated}</div>
                  <div className="text-xs text-indigo-600 dark:text-indigo-400">Days Allocated</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                  <div className="text-base font-bold text-emerald-700 dark:text-emerald-400">{leaveData?.utilization.totalUsed}</div>
                  <div className="text-xs text-emerald-600 dark:text-emerald-400">Days Used</div>
                </div>
              </div>
              <div className="text-center p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                <div className="text-lg font-bold text-violet-700 dark:text-violet-400">{leaveData?.utilization.utilizationRate}%</div>
                <div className="text-xs text-violet-600 dark:text-violet-400">Overall Utilization Rate</div>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">By Leave Type</h4>
                <div className="space-y-2">
                  {leaveData?.utilization.byLeaveType.map((type, index) => (
                    <div key={index}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">{type.type}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{type.rate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            type.rate > 80 ? 'bg-rose-500' :
                            type.rate > 60 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ width: `${Math.min(type.rate, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Used: {type.used}</span>
                        <span>Allocated: {type.allocated}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monthly Trend */}
        {view === 'overview' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Calendar size={14} className="text-indigo-600 dark:text-indigo-400" />
              Monthly Trend
            </h3>
            <div className="space-y-2">
              {leaveData?.monthlyTrend.slice(-6).map((month, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <div className="text-xs font-medium text-gray-900 dark:text-white">{month.month}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{month.popularType}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white text-xs">{month.requests} requests</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{month.approvalRate}% approved</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {view === 'overview' ? 'Approval Trends' :
             view === 'types' ? 'Type Popularity' :
             view === 'departments' ? 'Department Comparison' : 'Utilization Trends'}
          </h3>
          <div className="flex items-center justify-center h-32 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Calendar className="h-6 w-6 text-gray-400" />
            <span className="ml-2 text-gray-500 dark:text-gray-400 text-xs">Chart visualization</span>
          </div>
        </div>
      </div>

      {/* Insights & Recommendations */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
        <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300 mb-2">Leave Management Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-indigo-800 dark:text-indigo-400">
          <div>Vacation leave accounts for {leaveData?.leaveTypeDistribution.find(t => t.leaveType === 'Vacation')?.percentage}% of all requests</div>
          <div>{leaveData?.departmentAnalysis.find(d => d.approvalRate === Math.max(...(leaveData?.departmentAnalysis.map(d => d.approvalRate) || [0])))?.department} department has highest approval rate</div>
          <div>Overall utilization rate at {leaveData?.utilization.utilizationRate}% of allocated days</div>
          <div>Peak leave season observed in {leaveData?.monthlyTrend.find(m => m.requests === Math.max(...(leaveData?.monthlyTrend.map(m => m.requests) || [0])))?.month}</div>
        </div>
      </div>

      {/* Alerts */}
      {leaveData?.utilization.byLeaveType.some(type => type.rate > 80) && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2 text-amber-700 dark:text-amber-400" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              High utilization detected in some leave types. Consider reviewing allocation policies.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveReports